/**
 * Notification Service
 * API calls for notification management
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

// Types
export type NotificationType = 'Info' | 'Warning' | 'Error' | 'Success' | 'Announcement';
export type NotificationScope = 'System' | 'Client' | 'User' | 'SystemAdmins';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  scope: NotificationScope;
  actionUrl?: string;
  actionLabel?: string;
  createdAt: string;
  expiresAt?: string;
  isRead: boolean;
  readAt?: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unreadCount: number;
}

export interface NotificationCountResponse {
  unreadCount: number;
}

export interface MarkAllReadResponse {
  markedCount: number;
}

export interface CreateNotificationRequest {
  title: string;
  message: string;
  type: string;
  actionUrl?: string;
  actionLabel?: string;
  expiresAt?: string;
}

export interface AdminNotification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  scope: NotificationScope;
  actionUrl?: string;
  actionLabel?: string;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
  clientId?: string;
  userId?: string;
  targetUserName?: string;
}

/**
 * Get notifications for the current user
 */
export const getNotifications = async (
  includeRead = false,
  limit = 20
): Promise<NotificationListResponse> => {
  const url = `${API_ENDPOINTS.NOTIFICATIONS.LIST}?includeRead=${includeRead}&limit=${limit}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    throw await createApiError(response);
  }

  return response.json();
};

/**
 * Get unread notification count (for badge polling)
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NOTIFICATIONS.COUNT);

  if (!response.ok) {
    throw await createApiError(response);
  }

  const data: NotificationCountResponse = await response.json();
  return data.unreadCount;
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId),
    { method: 'POST' }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<number> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ,
    { method: 'POST' }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }

  const data: MarkAllReadResponse = await response.json();
  return data.markedCount;
};

/**
 * Dismiss a notification (permanently removes from list)
 */
export const dismissNotification = async (notificationId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.DISMISS(notificationId),
    { method: 'POST' }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }
};

// Admin functions

/**
 * Create a system-wide notification (ClientAdmin only)
 */
export const createSystemNotification = async (
  request: CreateNotificationRequest
): Promise<Notification> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.CREATE_SYSTEM,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }

  return response.json();
};

/**
 * Create a notification visible only to system administrators (ClientAdmin or SystemAdmin)
 */
export const createSystemAdminsNotification = async (
  request: CreateNotificationRequest
): Promise<Notification> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.CREATE_SYSTEM_ADMINS,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }

  return response.json();
};

/**
 * Create a client-wide notification (ClientAdmin only)
 */
export const createClientNotification = async (
  clientId: string,
  request: CreateNotificationRequest
): Promise<Notification> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.CREATE_CLIENT(clientId),
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }

  return response.json();
};

/**
 * Create a user-specific notification (ClientAdmin only)
 */
export const createUserNotification = async (
  userId: string,
  request: CreateNotificationRequest
): Promise<Notification> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.CREATE_USER(userId),
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }

  return response.json();
};

/**
 * Deactivate a notification (ClientAdmin only)
 */
export const deactivateNotification = async (notificationId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NOTIFICATIONS.DEACTIVATE(notificationId),
    { method: 'DELETE' }
  );

  if (!response.ok) {
    throw await createApiError(response);
  }
};

/**
 * Get notifications created by the current admin user (for management view)
 */
export const getCreatedNotifications = async (
  includeDeactivated = false
): Promise<AdminNotification[]> => {
  const url = `${API_ENDPOINTS.NOTIFICATIONS.GET_CREATED}?includeDeactivated=${includeDeactivated}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    throw await createApiError(response);
  }

  return response.json();
};
